import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

// Swiper is loaded globally via script tag
/** @global {any} Swiper */
/** @typedef {Window & { Swiper?: any }} WindowWithSwiper */

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        row.remove();
        // Trigger Swiper reinitialization after removal
        setTimeout(() => {
          this.#reinitializeSwiper();
        }, 100);
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const actionName = config.action === 'add' ? 'change' : config.action;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${actionName}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // Reinitialize Swiper if it exists and needs to be reinitialized
    this.#reinitializeSwiper();
    
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Reinitializes Swiper instances that may have been destroyed during cart updates
   */
  #reinitializeSwiper() {
    const swiperElement = /** @type {HTMLElement & { swiper?: any }} */ (document.querySelector(`#swiper-${this.sectionId}`));
    if (!swiperElement || typeof (/** @type {WindowWithSwiper} */ (window)).Swiper === 'undefined') return;

    // Store current slide position before destroying
    let currentSlide = 0;
    if (swiperElement.swiper) {
      currentSlide = swiperElement.swiper.activeIndex || 0;
      swiperElement.swiper.destroy(true, true);
    }
    
    // Reinitialize with preserved slide position
    this.#initSwiperWithPosition(currentSlide);
  }
  
  /**
   * Initializes Swiper with a specific slide position
   */
  #initSwiperWithPosition(/** @type {number} */ currentSlide) {
    const swiperElement = /** @type {HTMLElement & { swiper?: any }} */ (document.querySelector(`#swiper-${this.sectionId}`));
    if (!swiperElement || typeof (/** @type {WindowWithSwiper} */ (window)).Swiper === 'undefined') return;

    // Ensure pagination container exists
    const paginationElement = document.querySelector(`.swiper-pagination-${this.sectionId}`);
    if (!paginationElement) {
      // If pagination doesn't exist, create it
      const swiperContainer = swiperElement.querySelector('.swiper-wrapper');
      if (swiperContainer) {
        const pagination = document.createElement('div');
        pagination.className = `swiper-pagination swiper-pagination-${this.sectionId}`;
        swiperElement.appendChild(pagination);
      }
    }

    // Initialize with the correct configuration
    const swiper = new (/** @type {WindowWithSwiper} */ (window)).Swiper(`#swiper-${this.sectionId}`, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      slidesOffsetAfter: 80,
      freeMode: {
        enabled: false, // Disable freeMode temporarily to fix slider
        sticky: false,
        momentumRatio: 0.5,
        minimumVelocity: 0.02,
      },
      watchSlidesProgress: true,
      centeredSlides: false,
      allowTouchMove: true,
      touchRatio: 1,
      threshold: 5,
      pagination: {
        el: `.swiper-pagination-${this.sectionId}`,
        clickable: true,
        dynamicBullets: false,
        renderBullet: function (/** @type {number} */ index, /** @type {string} */ className) {
          return '<span class="' + className + '"></span>';
        },
      },
      breakpoints: {
        750: {
          enabled: false,
        },
      },
      on: {
        init: function() {
          // Restore previous slide position if it exists
          const swiperInstance = /** @type {any} */ (this);
          if (currentSlide >= 0 && currentSlide < swiperInstance.slides.length) {
            swiperInstance.slideTo(currentSlide, 0); // 0ms transition for instant move
          }
          
          // Initialize pagination bullets after slide position is set
          setTimeout(() => {
            swiperInstance.updatePagination();
          }, 50);
        },
        slideChange: function() {
          // Update pagination bullets on slide change
          const swiperInstance = /** @type {any} */ (this);
          swiperInstance.updatePagination();
        },
        slideChangeTransitionEnd: function() {
          // Ensure pagination is correct after transition ends
          const swiperInstance = /** @type {any} */ (this);
          swiperInstance.updatePagination();
        },
      },
    });
    
    // Add custom updatePagination method to the swiper instance
    swiper.updatePagination = function() {
      if (!this.pagination || !this.pagination.bullets) return;
      
      const activeIndex = this.activeIndex || 0;
      
      this.pagination.bullets.forEach((/** @type {HTMLElement} */ bullet, /** @type {number} */ index) => {
        const isActive = index === activeIndex;
        bullet.classList.toggle('swiper-pagination-bullet-active', isActive);
      });
    };
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
